
import React from 'react';
import './DatosInteres.css';

import MapaImg       from '../../Images/MapaEscalonilla.png';
import HistoriaImg   from '../../Images/HistoriaEscalonilla.jpg';
import DemografiaImg from '../../Images/DemografiaEscalonilla.jpg';
import EscudoImg     from '../../Images/Escudo.png';

const DatosInteres = () => (
  <div className="datos-interes-container">
    <h1>Datos de Interés sobre Escalonilla</h1>
    <p className="intro-text">
      Una panorámica del municipio: geografía, toponimia, población, callejero
      y los hitos que han ido modelando la identidad escalonillera.
    </p>

    {/* ────────────────────────────────── 1. GEOGRAFÍA ───────────────────────────────── */}
    <section className="dato-section">
      <h2>Ubicación y entorno natural</h2>
      <div className="dato-content">
        <div className="dato-text">
          <p>
            Escalonilla se asienta en el <strong>valle bajo del Tajo</strong>, 29 km al oeste
            de Toledo, a 498 m de altitud. Limita con Santo Domingo-Caudilla
            (N), Gerindote y Burujón (E), La Puebla de Montalbán (S) y Carmena
            (O). El término lo cruzan la <em>Cañada Real Segoviana</em> y los arroyos
            de la Soledad, del Barco y del Valle, todos estacionales. Al sureste
            brota el manantial del <strong>Charcón</strong>; sus marismas invernales atraen
            avefría, garza real y numerosas limícolas.
          </p>
        </div>
        <div className="dato-image">
          <img src={MapaImg} alt="Ubicación de Escalonilla" />
        </div>
      </div>
    </section>

    {/* ──────────────────────────────── 2. NOMBRE Y ESCUDO ────────────────────────────── */}
    <section className="dato-section">
      <h2>Nombre y escudo de la villa</h2>
      <div className="dato-content">
        <div className="dato-text">
          <p>
            <strong>Etimología.</strong> El filólogo Pedro González sostiene que
            <em>Escalonilla</em> es un diminutivo de <em>Escalona</em>, pero no alude a
            “escalón” sino al latín <em>ascalonia</em> = “cebolla de Ascalón” (el actual
            chalote). Nebrija ya recoge en su <em>Vocabulario Romance-Latín</em>
            (1495) la voz <em>escalona = cebolla</em>. El topónimo equivaldría, pues, a
            “cebollita”, como ocurre con el próximo Cebolla (Toledo).
          </p>
          <p>
            <strong>Blasón municipal.</strong> En 1993 se aprobó el escudo actual:
            <br/>• <b>Escalera</b> de sinople: armas parlantes (<em>scala</em> → Escalona).<br/>
            • <b>Tres estrellas</b> azules: los despoblados que se integraron en
              Escalonilla (Casas Albas, Veragüe y Nohalos).<br/>
            • <b>Madeja de estambre</b> de gules: la célebre industria de
              estameñas (s. XVI-XIX).<br/>
            Bordura de oro y corona real cierran el conjunto.
          </p>
        </div>
        <div className="dato-image">
          <img className="escudo-mini" src={EscudoImg} alt="Escudo de Escalonilla" />
        </div>
      </div>
    </section>

    {/* ───────────────────────────────── 3. CALLEJERO ────────────────────────────────── */}
    <section className="dato-section">
      <h2>Callejero con historia</h2>
      <div className="dato-content">
        <div className="dato-text">
          <p>
            El primer plano detallado data de 1988. Hoy sumamos <b>49 calles,
            16 callejones y 7 plazas</b>. Sus nombres se agrupan —según la
            clasificación de Alejandro Pinel— en siete bloques:
          </p>
          <ul>
            <li><b>Fundacionales</b> – Veragüe rememora la aldea absorbida en 1507.</li>
            <li><b>Religiosos</b> – Magdalena, San Germán, Cristo, Estrella o San Isidro.</li>
            <li><b>Vías de relación</b> – Toledo, Torrijos, La Puebla, Carmena…</li>
            <li><b>Usos y oficios</b> – Sileras, Molino, Tejar, Fraguas, Estrecha…</li>
            <li><b>Homenajes</b> – Maestro Julio Fuentes, Plaza de la Hispanidad, Rota,
                Quinto Centenario, Corporación.</li>
            <li><b>Nombres pintorescos</b> sin etimología clara: Triana, Falderín, Juno…</li>
          </ul>
          <p>
            Muchos topónimos populares persisten —Plaza de Arriba, El Barranco,
            Callejón de las c…— pese al registro oficial. El callejero es, en
            definitiva, <em>una crónica viva de las prioridades de cada época</em>.
          </p>
        </div>
      </div>
    </section>

    {/* ──────────────────────────────── 4. DEMOGRAFÍA ───────────────────────────────── */}
    <section className="dato-section">
      <h2>Demografía y curiosidades</h2>
      <div className="dato-content">
        <div className="dato-text">
          <p>
            <strong>Padrón 2024 – 1 497 hab.</strong> (771 mujeres · 726 varones). La
            pirámide acusa un acusado envejecimiento: solo el 14 % es menor de
            15 años mientras que el 27 % supera los 65 años.
          </p>
          <p>
            <strong>Origen.</strong> El 90 % nació en la provincia de Toledo; el resto procede
            de 22 provincias (destacan Madrid 94, Cáceres 8, Valladolid 6). Se
            contabilizan <b>96 municipios de nacimiento</b>; 825 vecinos son
            escalonilleros de cuna y 400 nacieron en Toledo capital.
          </p>
          <p>
            <strong>Nivel educativo.</strong> 42 % sin estudios (población muy mayor), 188 con
            Certificado de Escolaridad, 181 Graduado, 31 F.P., 33 Bachiller,
            29 titulados universitarios (14 de ellos maestros).
          </p>
          <p>
            <strong>Nombres y apellidos.</strong> 573 nombres diferentes; los más comunes:
            Jesús (30), Francisco (25) y Mariano/Carmen/María (18). 147 apellidos
            distintos; Gómez (109) y Jiménez (88) encabezan la lista. Dos únicas
            coincidencias completas: <em>María Teresa Gómez García</em> y
            <em>Carmen Rodríguez Gallardo</em>.
          </p>
          <p>
            <strong>Longevidad.</strong> Seis vecinos superan el siglo de vida —doña Paulina
            Muñoz Hernández (b. 1892) es la decana— y otros diez pasan de 90
            años.
          </p>
        </div>
        <div className="dato-image">
          <img src={DemografiaImg} alt="Pirámide de población de Escalonilla" />
        </div>
      </div>
    </section>

    {/* ───────────────────────────────── 5. HISTORIA ─────────────────────────────────── */}
    <section className="dato-section">
      <h2>Síntesis histórica</h2>
      <div className="dato-content">
        <div className="dato-text">
          <p>
            <strong>Antigüedad.</strong> Epígrafes latinos (s. I) prueban un asentamiento
            hispanorromano agrícola. En el s. XIII aparece citada como
            “alquería” en un documento mozárabe.
          </p>
          <p>
            <strong>Edad Media.</strong> Tres aldeas —Casas Albas, Veragüe y Nohalos—
            conforman la <em>tri raíz</em> del actual municipio; se despueblan entre 1490
            y 1507 y sus vecinos se integran en Escalonilla, que seguirá siendo
            lugar de realengo.
          </p>
          <p>
            <strong>S. XVI-XVIII – La estameña.</strong> Falta de propiedad agraria ⇒ auge de
            los telares: 60 en 1746; las estameñas de Escalonilla compiten con
            las inglesas. La cofradía gremial de San José (1619) regula el oficio.
          </p>
          <p>
            <strong>Templo parroquial.</strong> La nave se hunde en 1577 (13 fallecidos);
            se reconsagra en 1583. Retablo barroco de Andrés de Huerta (1712),
            órgano de Diego de Céspedes (1742) y lienzo del <em>Tránsito</em>.
          </p>
          <p>
            <strong>S. XIX.</strong> El cólera de 1834 origina el voto al <em>Cristo de la Cruz
            a Cuestas</em>, fiesta mayor desde entonces. En 1880 se levantan las
            Casas Consistoriales y se ajardina la Plaza de España.
          </p>
          <p>
            <strong>S. XX-XXI.</strong> Pico demográfico en 1933 (4 325 hab.). Migraciones
            rurales y mecanización reducen la población a los 1 500 actuales,
            que conservan un notable tejido asociativo y un patrimonio
            religioso-popular muy vivo.
          </p>
        </div>
        <div className="dato-image">
          <img src={HistoriaImg} alt="Vista histórica de Escalonilla" />
        </div>
      </div>
    </section>
  </div>
);

export default DatosInteres;
